import React, { useCallback, useEffect, useState } from 'react';
import { useRecordContext, useNotify } from 'react-admin';
import {
  Box, Chip, CircularProgress, IconButton, MenuItem, Pagination, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DEFAULT_API_ADMIN } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';

type GuessStatus = 'all' | 'ticket_only' | 'submitted' | 'winner' | 'loser';

interface GuessRow {
  id: string;
  customer_id: string;
  name: string;
  cellphone: string;
  cpf: string;
  score_home: number | null;
  score_away: number | null;
  submitted_at: string | null;
  created_at: string;
  is_winner: boolean | null;
  evaluated_at: string | null;
  prize_achievement_id: string | null;
  guess_status: 'ticket_only' | 'submitted' | 'winner' | 'loser';
}

interface ListResponse {
  bolao: { id: string; status: string; officialScoreHome: number | null; officialScoreAway: number | null };
  total: number;
  page: number;
  perPage: number;
  guesses: GuessRow[];
}

const STATUS_LABEL: Record<GuessRow['guess_status'], { label: string; color: 'default' | 'primary' | 'info' | 'success' | 'warning' }> = {
  ticket_only: { label: 'Sem palpite', color: 'warning' },
  submitted: { label: 'Palpitou', color: 'info' },
  winner: { label: '🎉 Ganhou', color: 'success' },
  loser: { label: 'Perdeu', color: 'default' },
};

const STATUS_CHOICES: Array<{ id: GuessStatus; name: string }> = [
  { id: 'all', name: 'Todos' },
  { id: 'ticket_only', name: 'Ganhou direito mas não palpitou' },
  { id: 'submitted', name: 'Palpitou (aguardando)' },
  { id: 'winner', name: 'Ganhou o prêmio' },
  { id: 'loser', name: 'Não acertou' },
];

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const formatCpf = (cpf?: string | null) => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatPhone = (phone?: string | null) => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return phone;
};

const BolaoGuessesTab: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const chainId = getChainId();

  const [status, setStatus] = useState<GuessStatus>('all');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ListResponse | null>(null);

  const fetchGuesses = useCallback(async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      // Converte "YYYY-MM-DD" (input date) em ISO no fuso LOCAL do browser
      // — input `type=date` não traz hora; queremos cobrir o dia inteiro local.
      // `new Date('2026-05-29')` é interpretado como UTC zero → manda registro
      // que aconteceu 22h BRT do dia anterior pra dentro do filtro. Fix:
      // construir Date com partes locais.
      const localStartOfDay = (s: string) => {
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
      };
      const localEndOfDay = (s: string) => {
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      };

      const params = new URLSearchParams();
      params.set('status', status);
      params.set('page', String(page));
      params.set('perPage', String(perPage));
      if (q.trim()) params.set('q', q.trim());
      if (dateFrom) params.set('dateFrom', localStartOfDay(dateFrom));
      if (dateTo) params.set('dateTo', localEndOfDay(dateTo));

      const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/boloes/${record.id}/guesses?${params.toString()}`;
      const { json } = await httpClientReactAdmin(url, { method: 'GET' });
      setData(json?.data ?? json);
    } catch (err: any) {
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Erro ao carregar palpites';
      notify(apiMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [chainId, record?.id, status, q, dateFrom, dateTo, page, perPage, notify]);

  useEffect(() => {
    fetchGuesses();
  }, [fetchGuesses]);

  if (!record) return null;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  const officialScore = data?.bolao.officialScoreHome != null
    ? `${data.bolao.officialScoreHome} × ${data.bolao.officialScoreAway}`
    : null;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '260px 1fr 180px 180px auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => { setStatus(e.target.value as GuessStatus); setPage(1); }}
          >
            {STATUS_CHOICES.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Buscar (nome, CPF, celular)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchGuesses(); } }}
          />
          <TextField
            size="small"
            label={status === 'ticket_only' ? 'Ganhou direito desde' : 'Palpitou desde'}
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Até"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <Tooltip title="Recarregar">
            <IconButton onClick={fetchGuesses} disabled={loading} size="small" sx={{ justifySelf: { xs: 'start', md: 'center' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {officialScore && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Placar oficial cadastrado: <strong>{officialScore}</strong>. Vencedores = acertaram exatamente este placar.
        </Typography>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {loading && !data ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress size={28} />
          </Box>
        ) : !data || data.guesses.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">Nenhum registro com esses filtros.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Celular</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Palpite</TableCell>
                  <TableCell>Ganhou direito em</TableCell>
                  <TableCell>Palpitou em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.guesses.map((row) => {
                  const tone = STATUS_LABEL[row.guess_status] || STATUS_LABEL.submitted;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Chip size="small" label={tone.label} color={tone.color} />
                      </TableCell>
                      <TableCell>{row.name || '—'}</TableCell>
                      <TableCell>{formatPhone(row.cellphone)}</TableCell>
                      <TableCell>{formatCpf(row.cpf)}</TableCell>
                      <TableCell>
                        {row.submitted_at
                          ? <strong>{row.score_home} × {row.score_away}</strong>
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell>{formatDate(row.created_at)}</TableCell>
                      <TableCell>{formatDate(row.submitted_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data && data.total > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5}>
            <Typography variant="caption" color="text.secondary">
              {data.total} {data.total === 1 ? 'registro' : 'registros'} • página {data.page} de {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BolaoGuessesTab;
